"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { parseNumberInput } from "@/lib/parse-number";

type SortDirection = "asc" | "desc";
type Separator = "newline" | "comma" | "space";

const SKIPPED_SHOWN = 20;

/*
 * Entries are separated by whitespace and semicolons. Commas also separate, unless a
 * semicolon is present anywhere: then the semicolon is the separator and a comma is a
 * decimal mark ("2,5; 3,5"), the rule `parseNumberList` documents. Either way a
 * whitespace-delimited entry written as a validly grouped integer ("1,000", "-12,345,678")
 * is one number, not a 1 and a 0.
 */
const SEMICOLON_SPLIT_RE = /[\s;]+/;
const WHITESPACE_SPLIT_RE = /\s+/;
const COMMA_SPLIT_RE = /,+/;
const GROUPED_THOUSANDS_RE = /^[+\-\u2212]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/;
const COMMAS_RE = /,/g;
const EDGE_COMMAS_RE = /^,+|,+$/g;
const TYPESET_MINUS_RE = /\u2212/;

export interface ParsedNumbers {
  numbers: number[];
  /** Entries that were not numbers, in the order they appeared. */
  skipped: string[];
}

function parseEntry(entry: string): number | null {
  if (GROUPED_THOUSANDS_RE.test(entry)) {
    return Number(entry.replace(COMMAS_RE, "").replace(TYPESET_MINUS_RE, "-"));
  }
  const value = parseNumberInput(entry);
  if (value !== null) {
    return value;
  }
  // Scientific and hexadecimal notation ("1e3", "0x10") have always been accepted.
  const fallback = Number(entry);
  return Number.isFinite(fallback) ? fallback : null;
}

export function parseNumbers(text: string): ParsedNumbers {
  const semicolons = text.includes(";");
  const numbers: number[] = [];
  const skipped: string[] = [];
  const entries = text
    .split(semicolons ? SEMICOLON_SPLIT_RE : WHITESPACE_SPLIT_RE)
    .filter((entry) => entry.length > 0);
  for (const rawEntry of entries) {
    // "1,000, 2,000": the comma that ends an entry is a separator, not part of it.
    const entry = semicolons ? rawEntry : rawEntry.replace(EDGE_COMMAS_RE, "");
    const parts =
      semicolons || GROUPED_THOUSANDS_RE.test(entry)
        ? [entry]
        : entry.split(COMMA_SPLIT_RE).filter((part) => part.length > 0);
    for (const part of parts) {
      const value = parseEntry(part);
      if (value === null) {
        skipped.push(part);
      } else {
        numbers.push(value);
      }
    }
  }
  return { numbers, skipped };
}

function sortNumbers(
  numbers: number[],
  direction: SortDirection,
  removeDuplicates: boolean
): number[] {
  let values = numbers.slice();
  if (removeDuplicates) {
    values = Array.from(new Set(values));
  }
  values.sort((a, b) => (direction === "asc" ? a - b : b - a));
  return values;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_SORTER_STRINGS = {
  sortOrder: "Sort order",
  ascending: "Ascending (small to large)",
  descending: "Descending (large to small)",
  outputSeparator: "Output separator",
  newLine: "New line",
  comma: "Comma",
  space: "Space",
  removeDuplicates: "Remove duplicates",
  inputLabel: "Input — numbers separated by lines, commas, or spaces",
  inputPlaceholder: "42\n7, 3.14\n-10 100",
  sortNumbers: "Sort Numbers",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  sortedOutput: "Sorted output",
  numbers: "Numbers",
  minimum: "Minimum",
  maximum: "Maximum",
  skipped: "Skipped, not numbers:",
} as const;

export default function NumberSorterWidget() {
  const [input, setInput] = useState("");
  const [direction, setDirection] = useState<SortDirection>("asc");
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [separator, setSeparator] = useState<Separator>("newline");
  const [output, setOutput] = useState("");
  const [sorted, setSorted] = useState<number[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(NUMBER_SORTER_STRINGS);

  const SEPARATORS: { value: Separator; label: string; joiner: string }[] = [
    { value: "newline", label: strings.newLine, joiner: "\n" },
    { value: "comma", label: strings.comma, joiner: ", " },
    { value: "space", label: strings.space, joiner: " " },
  ];

  function handleSort() {
    const { numbers, skipped: skippedEntries } = parseNumbers(input);
    setSkipped(skippedEntries);
    if (numbers.length === 0) {
      setSorted([]);
      setOutput("");
      return;
    }
    const values = sortNumbers(numbers, direction, removeDuplicates);
    const joiner =
      SEPARATORS.find((s) => s.value === separator)?.joiner ?? "\n";
    setSorted(values);
    setOutput(values.join(joiner));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setSorted([]);
    setSkipped([]);
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.sortOrder}
        </p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={direction === "asc"}
              className="accent-primary"
              name="number-direction"
              onChange={() => setDirection("asc")}
              type="radio"
              value="asc"
            />
            {strings.ascending}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={direction === "desc"}
              className="accent-primary"
              name="number-direction"
              onChange={() => setDirection("desc")}
              type="radio"
              value="desc"
            />
            {strings.descending}
          </label>
        </div>
        <p className="font-medium text-foreground text-sm">
          {strings.outputSeparator}
        </p>
        <div className="flex flex-wrap gap-3">
          {SEPARATORS.map((s) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={s.value}
            >
              <input
                checked={separator === s.value}
                className="accent-primary"
                name="number-separator"
                onChange={() => setSeparator(s.value)}
                type="radio"
                value={s.value}
              />
              {s.label}
            </label>
          ))}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={removeDuplicates}
            className="accent-primary"
            onChange={(e) => setRemoveDuplicates(e.target.checked)}
            type="checkbox"
          />
          {strings.removeDuplicates}
        </label>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="number-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="number-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleSort}
          type="button"
        >
          {strings.sortNumbers}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="number-output"
          >
            {strings.sortedOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="number-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {skipped.length > 0 && (
        <p aria-live="polite" className="text-muted-foreground text-sm">
          {strings.skipped} {skipped.slice(0, SKIPPED_SHOWN).join(", ")}
          {skipped.length > SKIPPED_SHOWN ? " …" : ""}
        </p>
      )}

      {/* Stats */}
      {sorted.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.numbers} value={sorted.length} />
          <StatCard label={strings.minimum} value={Math.min(...sorted)} />
          <StatCard label={strings.maximum} value={Math.max(...sorted)} />
        </div>
      )}
    </>
  );
}
